/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import {
  CommentDto,
  CreateTradeTalkDto,
  TradeTalkBaseResponse,
  TradeTalkCategory,
  TradeTalkCommentResponse,
  TradeTalkPrivacy,
  TradeTalkResponse,
  TradeTalkTag,
  UpdateTradeTalkDto,
} from '@monorepo/client-types';
import type { NotificationName } from '@monorepo/notification-common';
import { getEnv } from '@hub/helpers/getters.store';

type TradeTalkFeed = TradeTalkBaseResponse<TradeTalkResponse[]> | null;

export class TradeTalkStore {
  @observable
  allTradeTalks: TradeTalkFeed = null;

  @observable
  picksTradeTalks: TradeTalkFeed = null;

  @observable
  myTradeTalks: TradeTalkFeed = null;

  @observable
  similarTradeTalks: TradeTalkFeed = null;

  @observable
  formCategories: TradeTalkCategory[] = [];

  @observable
  tradeTalkTags: TradeTalkTag[] = [];

  @observable
  currentTradeTalk: TradeTalkResponse | null = null;

  @observable
  currentTradeTalkComments:
    | (TradeTalkBaseResponse<TradeTalkCommentResponse[]> & {
        tradeTalkId: string;
      })
    | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  getTradeTalk = async (id: string, forceRefresh = false) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();
      if (!forceRefresh && this.currentTradeTalk?.id === id) {
        return this.currentTradeTalk;
      }
      const tradeTalk = await tradeTalkService.getTradeTalk(id);
      runInAction(() => {
        this.currentTradeTalk = tradeTalk;
      });

      return tradeTalk;
    } catch (error) {
      runInAction(() => {
        this.currentTradeTalk = null;
      });
      console.error(`Failed getting trade-talk: ${id}`, error);
      throw error;
    }
  };

  async getTradeTalkComments(
    tradeTalkId: string,
    skip: number,
    limit: number,
    isLoggedIn = true,
  ) {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const tradeTalkComments = isLoggedIn
        ? await tradeTalkService.getTradeTalkComments(tradeTalkId, skip, limit)
        : await tradeTalkService.getPublicTradeTalkComments(
            tradeTalkId,
            skip,
            limit,
          );

      const isCommentsWithoutPaginationSkip = skip === 0;
      const currentTradeTalkCommentsBelongToAnotherTradeTalk =
        this.currentTradeTalkComments?.tradeTalkId !== tradeTalkId;

      const needToResetData =
        isCommentsWithoutPaginationSkip ||
        currentTradeTalkCommentsBelongToAnotherTradeTalk;

      runInAction(() => {
        if (needToResetData) {
          this.currentTradeTalkComments = { ...tradeTalkComments, tradeTalkId };
        } else {
          this.currentTradeTalkComments = {
            data: [
              ...this.currentTradeTalkComments!.data,
              ...tradeTalkComments.data,
            ],
            total: tradeTalkComments.total,
            tradeTalkId,
          };
        }
      });

      return tradeTalkComments;
    } catch (error) {
      console.error(
        `Failed getting trade-talk comments: ${tradeTalkId}`,
        error,
      );
      throw error;
    }
  }

  getAllTradeTalks = async (
    skip?: number,
    limit?: number,
    search?: string,
    authorId?: string,
    sortByOption?: string,
  ) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const allTradeTalks = await tradeTalkService.getAllTradeTalks(
        skip,
        limit,
        search,
        sortByOption,
      );
      this.updateTradeTalkFeed(allTradeTalks, 'myTradeTalks');

      return allTradeTalks;
    } catch (error) {
      console.error('Failed getting all trade-talks', error);
      throw error;
    }
  };

  createTradeTalk = async (tradeTalk: CreateTradeTalkDto) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const tradeTalkCreated =
        await tradeTalkService.createTradeTalk(tradeTalk);
      await this.getTags(true);
      if (this.myTradeTalks) {
        this.updateTradeTalkFeed(
          { data: [tradeTalkCreated], total: this.myTradeTalks.total },
          'myTradeTalks',
          'createNewTradeTalk',
        );
      }
      if (
        this.allTradeTalks &&
        tradeTalkCreated.privacy === TradeTalkPrivacy.Public
      ) {
        this.updateTradeTalkFeed(
          { data: [tradeTalkCreated], total: this.allTradeTalks.total },
          'allTradeTalks',
          'createNewTradeTalk',
        );
      }

      return tradeTalkCreated;
    } catch (error) {
      console.error('Failed creating trade-talk', error);
      throw error;
    }
  };

  deleteTradeTalk = async (id: string) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const deletedTradeTalk = await tradeTalkService.deleteTradeTalk(id);

      this.filterTradeTalkFeedById(id, 'myTradeTalks');
      this.filterTradeTalkFeedById(id, 'allTradeTalks');
      this.filterTradeTalkFeedById(id, 'picksTradeTalks');

      return deletedTradeTalk;
    } catch (e) {
      console.error(`Failed deleting trade-talk: ${id}`, e);
      throw e;
    }
  };

  updateTradeTalk = async (id: string, tradeTalk: UpdateTradeTalkDto) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const tradeTalkUpdated = await tradeTalkService.updateTradeTalk(
        id,
        tradeTalk,
      );
      await this.getTags(true);
      this.updatedTradeTalkFromFeed(id, tradeTalkUpdated, 'myTradeTalks');
      this.updatedTradeTalkFromFeed(id, tradeTalkUpdated, 'allTradeTalks');
      this.updatedTradeTalkFromFeed(id, tradeTalkUpdated, 'picksTradeTalks');
      this.updatedTradeTalkFromFeed(id, tradeTalkUpdated, 'similarTradeTalks');

      if (this.currentTradeTalk?.id === id) {
        this.currentTradeTalk = tradeTalkUpdated;
      }

      return tradeTalkUpdated;
    } catch (error) {
      console.error(`Failed updating trade-talk ${id}`, error);
      throw error;
    }
  };

  getCategories = async () => {
    try {
      if (this.formCategories.length) return this.formCategories;

      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const categories = await tradeTalkService.getCategories();

      runInAction(() => {
        this.formCategories = categories;
      });

      return categories;
    } catch (error) {
      console.error('Failed getting categories.', error);
      throw error;
    }
  };

  getTags = async (reFetch = false) => {
    try {
      if (this.tradeTalkTags.length && !reFetch) return this.tradeTalkTags;
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const tags = await tradeTalkService.getTags();
      runInAction(() => {
        this.tradeTalkTags = tags;
      });
      return tags;
    } catch (error) {
      console.error('Failed getting trade-talks tags', error);
      throw error;
    }
  };

  async createTradeTalkNotification(notificationName: NotificationName) {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();
      return tradeTalkService.createTradeTalkNotification(notificationName);
    } catch (error) {
      console.error(
        'Failed creating new notification for trade-talk published',
        error,
      );
      throw error;
    }
  }

  getAllPublicTradeTalks = async (
    skip?: number,
    limit?: number,
    search?: string,
    authorId?: string,
    sortByOption?: string,
  ) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const allTradeTalks = await tradeTalkService.getAllPublicTradeTalks(
        skip,
        limit,
        search,
        authorId,
        sortByOption,
      );
      this.updateTradeTalkFeed(allTradeTalks, 'allTradeTalks');

      return allTradeTalks;
    } catch (error) {
      console.error('Failed getting all trade-talks', error);
      throw error;
    }
  };

  get5ersPickTradeTalks = async (
    skip?: number,
    limit?: number,
    search?: string,
    authorId?: string,
    sortByOption?: string,
  ) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const allTradeTalks = await tradeTalkService.get5ersPickTradeTalks(
        skip,
        limit,
        search,
        sortByOption,
      );
      this.updateTradeTalkFeed(allTradeTalks, 'picksTradeTalks');

      return allTradeTalks;
    } catch (error) {
      console.error('Failed 5ers`s picks trade-talks', error);
      throw error;
    }
  };

  getSimilarTradeTalks = async (id: string, limit: number) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();
      const res = await tradeTalkService.getSimilarTradeTalks(id, limit);

      runInAction(() => {
        this.similarTradeTalks = res;
      });

      return res;
    } catch (error) {
      console.error(`Failed getting similar trade-talks for id: ${id}`, error);
      throw error;
    }
  };

  getSinglePublicTradeTalkForGuests = async (id: string) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const tradeTalk =
        await tradeTalkService.getSinglePublicTradeTalkForGuests(id);
      runInAction(() => {
        this.currentTradeTalk = tradeTalk;
      });

      return tradeTalk;
    } catch (error) {
      runInAction(() => {
        this.currentTradeTalk = null;
      });
      console.error(`Failed getting trade-talk: ${id}`, error);
      throw error;
    }
  };

  getPublicTradeTalksForGuests = async (
    skip?: number,
    limit?: number,
    search?: string,
    authorId?: string,
    sortByOption?: string,
  ) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const allTradeTalks = await tradeTalkService.getPublicTradeTalksForGuests(
        skip,
        limit,
        search,
        sortByOption,
      );

      this.updateTradeTalkFeed(allTradeTalks, 'allTradeTalks');

      return allTradeTalks;
    } catch (error) {
      console.error('Failed getting public trade-talks', error);
      throw error;
    }
  };

  get5ersPickTradeTalksForGuests = async (
    skip?: number,
    limit?: number,
    search?: string,
    authorId?: string,
    sortByOption?: string,
  ) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const picks = await tradeTalkService.get5ersPickTradeTalksForGuests(
        skip,
        limit,
        search,
        sortByOption,
      );

      this.updateTradeTalkFeed(picks, 'picksTradeTalks');

      return picks;
    } catch (error) {
      console.error('Failed getting public trade-talks', error);
      throw error;
    }
  };

  getPublicSimilarTradeTalksForGuests = async (id: string, limit: number) => {
    try {
      let publicTradeTalks = this.allTradeTalks;
      let publicPicksTradeTalks = this.picksTradeTalks;
      if (!publicTradeTalks?.total) {
        publicTradeTalks = await this.getPublicTradeTalksForGuests(0, limit);
      }
      if (!publicPicksTradeTalks?.total) {
        publicPicksTradeTalks = await this.get5ersPickTradeTalksForGuests(
          0,
          limit,
        );
      }
      const allPublicTradeTalks = [
        ...publicTradeTalks.data,
        ...publicPicksTradeTalks.data,
      ].filter((tradeTalk) => tradeTalk.id !== id);

      const res = allPublicTradeTalks.slice(0, limit);

      runInAction(() => {
        this.similarTradeTalks = { data: res, total: res.length };
      });

      return res;
    } catch (error) {
      console.error(`Failed getting public trade-talk: ${id}`, error);
      throw error;
    }
  };

  getAllPublicTradeTalksByUserPublicId = async (publicId: string) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      return tradeTalkService.getAllPublicTradeTalksByUserPublicId(publicId);
    } catch (error) {
      console.error('Failed getting all trade-talks', error);
      throw error;
    }
  };

  @action
  private filterTradeTalkFeedById = (
    tradeTalkId: string,
    tradeTalksFeed: keyof Pick<
      TradeTalkStore,
      'allTradeTalks' | 'picksTradeTalks' | 'myTradeTalks'
    >,
  ) => {
    if (this[tradeTalksFeed]) {
      const newTradeTalkFeed = this[tradeTalksFeed].data.filter(
        (tradeTalk) => tradeTalk.id !== tradeTalkId,
      );
      if (newTradeTalkFeed.length !== this[tradeTalksFeed].data.length) {
        this[tradeTalksFeed] = {
          data: newTradeTalkFeed,
          total: this[tradeTalksFeed].total - 1,
        };
      }
    }
  };

  @action
  private updateTradeTalkFeed = (
    newTradeTalks: TradeTalkBaseResponse<TradeTalkResponse[]>,
    tradeTalksFeed: keyof Pick<
      TradeTalkStore,
      'allTradeTalks' | 'myTradeTalks' | 'picksTradeTalks'
    >,
    updateType?: 'createNewTradeTalk',
  ) => {
    if (!this[tradeTalksFeed]) {
      this[tradeTalksFeed] = newTradeTalks;
    } else {
      if (updateType === 'createNewTradeTalk') {
        if (this[tradeTalksFeed].data.length) {
          this[tradeTalksFeed] = {
            data: [...newTradeTalks.data, ...this[tradeTalksFeed].data],
            total: newTradeTalks.total + 1,
          };
        }
      } else {
        this[tradeTalksFeed] = {
          data: [...this[tradeTalksFeed].data, ...newTradeTalks.data],
          total: newTradeTalks.total,
        };
      }
    }
  };

  @action
  private updatedTradeTalkFromFeed = (
    id: string,
    updatedTradeTalk: TradeTalkResponse,
    tradeTalksFeed: keyof Pick<
      TradeTalkStore,
      'allTradeTalks' | 'myTradeTalks' | 'picksTradeTalks' | 'similarTradeTalks'
    >,
  ) => {
    if (this[tradeTalksFeed]) {
      const tradeTalksIsInFeed = this[tradeTalksFeed].data.find(
        (tradeTalk) => tradeTalk.id === id,
      );
      if (tradeTalksIsInFeed) {
        this[tradeTalksFeed] = {
          ...this[tradeTalksFeed],
          data: this[tradeTalksFeed].data.map((tradeTalk) =>
            tradeTalk.id === id ? updatedTradeTalk : tradeTalk,
          ),
        };
      }
    }
  };

  restartFeed = (
    tradeTalksFeed: keyof Pick<
      TradeTalkStore,
      'allTradeTalks' | 'picksTradeTalks' | 'myTradeTalks'
    >,
  ) => {
    this[tradeTalksFeed] = null;
  };

  reportTradeTalk = async (id: string, reason: string) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const tradeTalk = await tradeTalkService.reportTradeTalk(id, reason);
      tradeTalk.isReportedByCurrentUser = true;
      this.updatedTradeTalkFromFeed(id, tradeTalk, 'allTradeTalks');
      this.updatedTradeTalkFromFeed(id, tradeTalk, 'picksTradeTalks');
      this.updatedTradeTalkFromFeed(id, tradeTalk, 'similarTradeTalks');
      if (this.currentTradeTalk?.id === id) {
        this.currentTradeTalk.isReportedByCurrentUser = true;
      }

      return tradeTalk;
    } catch (error) {
      console.error('Failed reporting trade-talk', error);
      throw error;
    }
  };

  private pendingLikeRequests = new Map<string, AbortController>();
  private pendingCommentLikeRequests = new Map<string, AbortController>();

  @action
  updateTradeTalkLike = async (id: string, userId: string) => {
    const existingController = this.pendingLikeRequests.get(id);
    if (existingController) {
      existingController.abort();
    }

    const abortController = new AbortController();
    this.pendingLikeRequests.set(id, abortController);

    const originalStates = this.captureCurrentTradeTalkStates(id);

    try {
      // This is to immediatelly reflect the like but in the background to process the request
      this.optimisticallyUpdateLike(id, userId);

      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const tradeTalk = await tradeTalkService.updateTradeTalkLike(id);

      if (abortController.signal.aborted) {
        return;
      }

      this.pendingLikeRequests.delete(id);

      const highlightsToPreserve = Object.values(originalStates).find(
        (state) => state?.highlights,
      )?.highlights;

      const tradeTalkWithHighlights = highlightsToPreserve
        ? { ...tradeTalk, highlights: highlightsToPreserve }
        : tradeTalk;

      this.updateAllFeeds(id, tradeTalkWithHighlights);
      return tradeTalkWithHighlights;
    } catch (error) {
      console.error('Failed updateTradeTalkLike', error);

      if (!abortController.signal.aborted) {
        this.rollbackTradeTalkStates(id, originalStates);
        this.pendingLikeRequests.delete(id);
      }

      throw error;
    }
  };

  private captureCurrentTradeTalkStates = (id: string) => {
    const states: Partial<Record<string, TradeTalkResponse>> = {};

    const feeds: Array<
      keyof Pick<
        TradeTalkStore,
        | 'allTradeTalks'
        | 'myTradeTalks'
        | 'picksTradeTalks'
        | 'similarTradeTalks'
      >
    > = [
      'allTradeTalks',
      'myTradeTalks',
      'picksTradeTalks',
      'similarTradeTalks',
    ];

    feeds.forEach((feedName) => {
      const feed = this[feedName];
      if (feed) {
        const tradeTalk = feed.data.find((t) => t.id === id);
        if (tradeTalk) {
          states[feedName] = { ...tradeTalk, likes: { ...tradeTalk.likes } };
        }
      }
    });

    return states;
  };

  private rollbackTradeTalkStates = (
    id: string,
    originalStates: Partial<Record<string, TradeTalkResponse>>,
  ) => {
    Object.entries(originalStates).forEach(([feedName, originalTradeTalk]) => {
      if (originalTradeTalk) {
        this.updatedTradeTalkFromFeed(id, originalTradeTalk, feedName as any);
      }
    });
  };

  private optimisticallyUpdateLike = (id: string, userId: string) => {
    const feeds: Array<
      keyof Pick<
        TradeTalkStore,
        | 'allTradeTalks'
        | 'myTradeTalks'
        | 'picksTradeTalks'
        | 'similarTradeTalks'
      >
    > = [
      'allTradeTalks',
      'myTradeTalks',
      'picksTradeTalks',
      'similarTradeTalks',
    ];

    feeds.forEach((feedName) => {
      const feed = this[feedName];
      if (feed) {
        const tradeTalk = feed.data.find((t) => t.id === id);
        if (tradeTalk) {
          const updatedLikes = { ...tradeTalk.likes };

          if (updatedLikes[userId]) {
            delete updatedLikes[userId];
          } else {
            // Add like - use empty strings for user data since this is optimistic
            // The real data will come from the server response
            updatedLikes[userId] = {
              author: '',
              userImg: '',
              email: '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
          }

          const updatedTradeTalk: TradeTalkResponse = {
            ...tradeTalk,
            likes: updatedLikes,
          };

          this.updatedTradeTalkFromFeed(id, updatedTradeTalk, feedName);
        }
      }
    });
  };

  @action
  updateCommentLike = async (id: string, userId: string) => {
    const existingController = this.pendingCommentLikeRequests.get(id);
    if (existingController) {
      existingController.abort();
    }

    const abortController = new AbortController();
    this.pendingCommentLikeRequests.set(id, abortController);

    const originalComment = this.currentTradeTalkComments?.data.find(
      (c) => c.id === id,
    );
    const originalLikes = originalComment ? { ...originalComment.likes } : null;
    const originalDislikes = originalComment
      ? { ...originalComment.dislikes }
      : null;

    try {
      if (this.currentTradeTalkComments && originalComment) {
        const updatedLikes = { ...originalComment.likes };
        const updatedDislikes = { ...originalComment.dislikes };

        if (updatedLikes[userId]) {
          delete updatedLikes[userId];
        } else {
          updatedLikes[userId] = {
            author: '',
            userImg: '',
            email: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          if (updatedDislikes[userId]) {
            delete updatedDislikes[userId];
          }
        }

        this.currentTradeTalkComments = {
          ...this.currentTradeTalkComments,
          data: this.currentTradeTalkComments.data.map((comment) =>
            comment.id === id
              ? { ...comment, likes: updatedLikes, dislikes: updatedDislikes }
              : comment,
          ),
        };
      }

      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const result = await tradeTalkService.updateCommentLike(id);

      if (abortController.signal.aborted) {
        return;
      }

      this.pendingCommentLikeRequests.delete(id);

      return result;
    } catch (error) {
      console.error('Failed updateCommentLike', error);

      if (!abortController.signal.aborted) {
        if (
          this.currentTradeTalkComments &&
          originalComment &&
          originalLikes &&
          originalDislikes
        ) {
          this.currentTradeTalkComments = {
            ...this.currentTradeTalkComments,
            data: this.currentTradeTalkComments.data.map((comment) =>
              comment.id === id
                ? {
                    ...comment,
                    likes: originalLikes,
                    dislikes: originalDislikes,
                  }
                : comment,
            ),
          };
        }
        this.pendingCommentLikeRequests.delete(id);
      }

      throw error;
    }
  };

  @action
  updateCommentDislike = async (id: string, userId: string) => {
    const existingController = this.pendingCommentLikeRequests.get(
      `dislike-${id}`,
    );
    if (existingController) {
      existingController.abort();
    }

    const abortController = new AbortController();
    this.pendingCommentLikeRequests.set(`dislike-${id}`, abortController);

    const originalComment = this.currentTradeTalkComments?.data.find(
      (c) => c.id === id,
    );
    const originalDislikes = originalComment
      ? { ...originalComment.dislikes }
      : null;
    const originalLikes = originalComment ? { ...originalComment.likes } : null;

    try {
      if (this.currentTradeTalkComments && originalComment) {
        const updatedDislikes = { ...originalComment.dislikes };
        const updatedLikes = { ...originalComment.likes };

        if (updatedDislikes[userId]) {
          delete updatedDislikes[userId];
        } else {
          updatedDislikes[userId] = {
            author: '',
            userImg: '',
            email: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          if (updatedLikes[userId]) {
            delete updatedLikes[userId];
          }
        }

        this.currentTradeTalkComments = {
          ...this.currentTradeTalkComments,
          data: this.currentTradeTalkComments.data.map((comment) =>
            comment.id === id
              ? { ...comment, dislikes: updatedDislikes, likes: updatedLikes }
              : comment,
          ),
        };
      }

      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const result = await tradeTalkService.updateCommentDislike(id);

      if (abortController.signal.aborted) {
        return;
      }

      this.pendingCommentLikeRequests.delete(`dislike-${id}`);

      return result;
    } catch (error) {
      console.error('Failed updateCommentDislike', error);

      if (!abortController.signal.aborted) {
        if (
          this.currentTradeTalkComments &&
          originalComment &&
          originalDislikes &&
          originalLikes
        ) {
          this.currentTradeTalkComments = {
            ...this.currentTradeTalkComments,
            data: this.currentTradeTalkComments.data.map((comment) =>
              comment.id === id
                ? {
                    ...comment,
                    dislikes: originalDislikes,
                    likes: originalLikes,
                  }
                : comment,
            ),
          };
        }
        this.pendingCommentLikeRequests.delete(`dislike-${id}`);
      }

      throw error;
    }
  };

  @action
  getCommentReplies = async (id: string, skip: number, limit: number) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      return await tradeTalkService.getCommentReplies(id, skip, limit);
    } catch (error) {
      console.error('Failed getCommentReplies', error);
      throw error;
    }
  };

  updateAllFeeds = (id: string, tradeTalk: TradeTalkResponse) => {
    this.updatedTradeTalkFromFeed(id, tradeTalk, 'myTradeTalks');
    this.updatedTradeTalkFromFeed(id, tradeTalk, 'allTradeTalks');
    this.updatedTradeTalkFromFeed(id, tradeTalk, 'picksTradeTalks');
    this.updatedTradeTalkFromFeed(id, tradeTalk, 'similarTradeTalks');
  };

  createTradeTalkComment = async (commentDto: CommentDto) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      const newComment =
        await tradeTalkService.createTradeTalkComment(commentDto);

      runInAction(() => {
        if (this.currentTradeTalkComments) {
          if (!commentDto.parentId) {
            this.currentTradeTalkComments = {
              ...this.currentTradeTalkComments,
              data: [newComment, ...this.currentTradeTalkComments.data],
              total: this.currentTradeTalkComments.total + 1,
            };
          } else {
            this.currentTradeTalkComments = {
              ...this.currentTradeTalkComments,
              data: this.currentTradeTalkComments.data.map((comment) =>
                comment.id === commentDto.parentId
                  ? {
                      ...comment,
                      repliesCount: (comment.repliesCount || 0) + 1,
                    }
                  : comment,
              ),
            };
          }
          this.updateCurrentTradeTalkCommentsCount(1);
        }
      });

      return newComment;
    } catch (error) {
      console.error('Failed creating trade-talk comment', error);
      throw error;
    }
  };

  updateCurrentTradeTalkCommentsCount = (amount: number) => {
    if (this.currentTradeTalk) {
      this.currentTradeTalk = {
        ...this.currentTradeTalk,
        commentsCount: this.currentTradeTalk.commentsCount + amount,
      };
    }
  };

  updateTradeTalkComment = async (id: string, content: string) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();
      await tradeTalkService.updateTradeTalkComment(id, content);
      this.currentTradeTalkComments = {
        ...this.currentTradeTalkComments!,
        data: this.currentTradeTalkComments!.data.map((comment) => {
          if (comment.id === id) {
            comment.content = content;
          }
          return comment;
        }),
      };
    } catch (error) {
      console.error('Failed updateTradeTalkComment', error);
      throw error;
    }
  };

  deleteComment = async (id: string) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      await tradeTalkService.deleteComment(id);
      runInAction(() => {
        this.currentTradeTalkComments = {
          ...this.currentTradeTalkComments!,
          data: this.currentTradeTalkComments!.data.filter(
            (comment) => comment.id !== id,
          ),
          total: this.currentTradeTalkComments!.total - 1,
        };

        this.updateCurrentTradeTalkCommentsCount(-1);
      });
    } catch (error) {
      console.error('Failed deleteComment', error);
      throw error;
    }
  };

  updateCommentReport = async (id: string) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      await tradeTalkService.updateCommentReport(id);
      runInAction(() => {
        this.currentTradeTalkComments = {
          ...this.currentTradeTalkComments!,
          data: this.currentTradeTalkComments!.data.map((comment) => {
            if (comment.id === id) {
              comment.isReportedByCurrentUser =
                !comment.isReportedByCurrentUser;
            }
            return comment;
          }),
        };
      });
    } catch (error) {
      console.error('Failed reportOnComment', error);
      throw error;
    }
  };

  getTradeTalkLikes = async (id: string, skip = 0, limit = 20) => {
    try {
      const {
        apiFactory: { tradeTalkService },
      } = getEnv();

      return await tradeTalkService.getTradeTalkLikes(id, skip, limit);
    } catch (error) {
      console.error(`Failed getting trade-talk likes: ${id}`, error);
      throw error;
    }
  };
}
